import { of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { answer, model } from './gpt.js';
import { buildPrompt } from './prompts.js';
import { nestMarkdown } from '../../backend/lib/markdown.js';

let maxDataCollectionQuestions = 4;

let categorizationFormat = {
    type: 'object',
    properties: {
        conversationLanguageName: {
            type: 'string',
            minLength: 2,
            maxLength: 56,
        },
        internetResearchSearchQueries: buildStringArrayFormat(2, 2, 64),
        scientificPaperSearchTerms: buildStringArrayFormat(2, 2, 64),
        topics: buildStringArrayFormat(4, 2, 48),
        summarizingQuestion: {
            type: 'string',
            minLength: 7,
            maxLength: 256,
        },
        dataCollectionQuestions: {
            type: 'array',
            items: {
                type: 'string',
                minLength: 7,
                maxLength: 256,
            },
            maxItems: maxDataCollectionQuestions,
        },
        productSearch: {
            type: 'boolean',
        },
    },
    required: [
        'internetResearchSearchQueries',
        'scientificPaperSearchTerms',
        'topics',
        'summarizingQuestion',
        'dataCollectionQuestions',
        'productSearch',
    ],
};

function buildStringArrayFormat(maxItems, minLength, maxLength) {
    return {
        type: 'array',
        items: {
            type: 'string',
            minLength,
            maxLength,
        },
        maxItems,
    };
}

export function categorizeConversation(messages) {
    let catRoles = new Set(['system', 'user', 'assistant']);
    let catMessages = [];
    for (let msg of messages) {
        if (catRoles.has(msg.role)) {
            catMessages.push(msg);
            continue;
        }
        // we once added msg.role === 'knowledge' messages
        // here. that lead to the situation that sometimes the
        // internet query was more influenced by the knowledge
        // than by the actual query formulated by the user. that's
        // why it got removed again.
    }
    return of(undefined).pipe(
        switchMap(() => {
            let lastMessage = catMessages[catMessages.length - 1];
            if (lastMessage?.role !== 'user') {
                throw new Error('Expected last message from user');
            }
            return buildPrompt('categorize', {
                maxDataCollectionQuestions,
                userRequest: nestMarkdown(lastMessage.content),
            });
        }),
        switchMap(prompt => {
            catMessages[catMessages.length - 1] = {
                role: 'user',
                content: prompt,
            };
            return answer(catMessages, {
                balancer_options: {
                    security_level: 100,
                },
                options: {
                    temperature: 1,
                },
                format: categorizationFormat,
            });
        }),
    ).pipe(
        map(msg => {
            let content = msg.message.content;
            try {
                // the LLM sometimes mistakes the double quotes
                // necessary for JSON strings
                return JSON.parse(content.replaceAll('“', '"'));
            } catch (e) {
                return JSON.parse(content);
            }
        }),
        map(cat => ({
            ...cat,
            internetResearchSearchQueries: fixLlmQueryJsonStrings(cat.internetResearchSearchQueries),
            scientificPaperSearchTerms: fixLlmQueryJsonStrings(cat.scientificPaperSearchTerms),
            topics: fixLlmQueryJsonStrings(cat.topics),
            dataCollectionQuestions: cat.dataCollectionQuestions,
        })),
    );
}

let llmQueryStringSplitPattern = /["”“],[\s]*["”“]/;

/**
 * fixLlmQueryJsononStrings fixes extra JSON control characters an the
 * beginning and end of strings that are produced by an LLM.
 */
function fixLlmQueryJsonStrings(queries) {
    if (!queries) {
        return queries;
    }
    let fragments = [];
    for (let q of queries) {
        for (let frag of q.split(llmQueryStringSplitPattern)) {
            fragments.push(frag);
        }
    }
    return fragments.map(trimLlmString).filter(s => !!s);
}

let trimLlmPattern = /^["”“, ]*(.*?)["”“, ]*$/;

function trimLlmString(s) {
    let m = s.match(trimLlmPattern);
    return m[1];
}
